import React, { useState, useRef, useEffect } from "react";
import styles from "./time.module.css";
import * as requests from "util/requests";
import { TextField, Checkbox } from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import ElementTitle from "ui/crfs/element_title";
import DividerLine from "ui/crfs/divider_line";
import { pad_time_value, parseTime } from "util/ui";
import DataCompleteChangeModal from "ui/crfs/data_complete_change_modal";
import ElementMenu from "ui/crfs/element_menu";

let tempStorage = null;
let newValueObj = null;

export default function Time(props) {
  const {
    field,
    title,
    options,
    sub_title,
    uval,
    onUpdate,
    crf_id,
    field_id,
    query,
    setQuery,
    subject_id,
    domain_id,
    calcUpdateToCrfStatus,
    enableQuery,
    show_checkmark,
    time_position = "value",
    crfStatus,
    read_only,
    toggleHistory,
    selected_audit_icon,
    setQueryModalData,
    captureCompleteDateChangeReason = false,
  } = props;
  const [oldValue, setOldValue] = useState(uval);
  const [hours, setHours] = useState("");
  const [hoursHasChanged, setHoursHasChanged] = useState(null);
  const [minutes, setMinutes] = useState("");
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const [valueObj, setValueObj] = useState((field && field.value_obj) || {});
  const [showingCompleteChangeModal, setShowingCompleteChangeModal] = useState(false);

  const setTimeFromObj = () => {
    let time = field.value_obj[time_position];
    time = parseTime(time);
    setHours(time.hours);
    setMinutes(time.minutes);
  };

  useEffect(() => {
    tempStorage = field.value_obj;
  }, [field]);

  useEffect(() => {
    setTimeFromObj();
  }, [field.value_obj]);

  const commonUpdateValueActions = (key, value, reason = null) => {
    //don't do anything if there is no value change
    if (parseTime(tempStorage[time_position])[key] === value) {
      return;
    }
    let obj = { ...valueObj };
    obj.value_name = field.value_name;
    obj.value2_name = field.value2_name;

    if (key === "hours" && (minutes || minutes === "")) {
      obj[time_position] = `${value}:${minutes}`;
    }
    if (key === "minutes" && (hours || hours === "")) {
      obj[time_position] = `${hours}:${value}`;
    }
    setValueObj(obj);

    if (RegExp(/[0-9]{2}:[0-9]{2}/).test(obj[time_position]) || obj[time_position] === ":") {
      //don't update upstream data, including tracking if the data entry is incomplete
      if (reason) {
        obj.reason = reason;
      }
      if (obj[time_position] === ":") {
        obj[time_position] = null;
      }
      onUpdate(obj);
      tempStorage = obj;
    }
  };
  const updateValue = (key, value) => {
    if (crfStatus === "data_complete" && captureCompleteDateChangeReason) {
      tempStorage = valueObj;
      newValueObj = { key, value };
      setShowingCompleteChangeModal(true);
      return;
    }
    commonUpdateValueActions(key, value);
  };

  const onKeyDownHours = (key) => {
    if (key.keyCode === 13) {
      hoursRef.current.blur();
    }
  };
  const onKeyDownMinutes = (key) => {
    if (key.keyCode === 13) {
      minutesRef.current.blur();
    }
  };

  const onFocusHours = (event) => {};

  useEffect(() => {
    if (
      hours &&
      hours.length === 2 &&
      hours < 24 &&
      (crfStatus !== "data_complete" || !captureCompleteDateChangeReason)
    )
      minutesRef.current && minutesRef.current.focus();
  }, [hoursHasChanged]);

  const closeModal = () => {
    setTimeFromObj();
    setShowingCompleteChangeModal(false);
  };
  const acceptTempStorage = (reason) => {
    commonUpdateValueActions(newValueObj.key, newValueObj[time_position], reason);
    newValueObj = null;
    setShowingCompleteChangeModal(false);
  };

  return (
    <div className={styles.outer_wrapper}>
      <div className={styles.input_flex}>
        <div className={styles.left_split}>
          <ElementTitle title={title} required={field.required} />
        </div>
        {read_only || field.read_only ? (
          <div className={[styles["right-split"], styles.time_entry].join(" ")}>
            <div className={styles.flex}>
              <div>{valueObj[time_position] || "--:--"}</div>
              <div className={styles.audit_icon}>
                <ElementMenu {...props} />
              </div>
            </div>
            <div> {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}</div>
          </div>
        ) : (
          <div className={[styles["right-split"], styles.time_entry].join(" ")}>
            <div className={styles.flex}>
              <div className={styles.time_wrapper}>
                <div>
                  <TextField
                    type="text"
                    inputRef={hoursRef}
                    placeholder="HH"
                    value={hours}
                    onChange={(e) => {
                      if (!/^([0-9]+){0,2}$/.test(e.target.value)) {
                        return;
                      }
                      if (hours <= 24) {
                        setHoursHasChanged(Math.random());
                      }
                      setHours(e.target.value);
                    }}
                    onBlur={(e) => {
                      if (hours === "") {
                        setHours("");
                        updateValue("hours", "");
                      } else if (hours < 24) {
                        let display = pad_time_value(hours);
                        setHours(display);
                        updateValue("hours", display);
                      }
                    }}
                    onKeyDown={onKeyDownHours}
                    inputProps={{ maxLength: 2 }}
                    autoComplete="off"
                    onFocus={onFocusHours}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "5px",
                        textAlign: "center",
                        width: "30px",
                      },
                    }}
                    error={hours >= 24}
                  />
                </div>
                <div className={styles.colon}>:</div>
                <div>
                  <TextField
                    type="text"
                    inputRef={minutesRef}
                    placeholder="MM"
                    value={minutes}
                    onChange={(e) => {
                      if (!/^([0-9]+){0,2}$/.test(e.target.value)) {
                        return;
                      }
                      setMinutes(e.target.value);
                    }}
                    onBlur={(e) => {
                      if (minutes === "") {
                        setMinutes("");
                        updateValue("minutes", "");
                      } else if (minutes < 60) {
                        let display = pad_time_value(minutes);
                        setMinutes(display);
                        updateValue("minutes", display);
                      }
                    }}
                    onKeyDown={onKeyDownMinutes}
                    inputProps={{ maxLength: 2 }}
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "5px",
                        textAlign: "center",
                        width: "30px",
                      },
                    }}
                    error={minutes >= 60}
                  />
                </div>
                <div className={styles.input_description}>(24h clock, HH:MM)</div>
              </div>

              <div className={styles.audit_icon}>
                <ElementMenu {...props} />
              </div>
            </div>
            <div> {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}</div>
          </div>
        )}
      </div>
      <DividerLine />
      <DataCompleteChangeModal isOpen={showingCompleteChangeModal} close={closeModal} onYes={acceptTempStorage} />
    </div>
  );
}
